import {
  type AreaLevelType,
  areaLevelAtom,
  firstAreaAtom,
  mapRefAtom,
  markersAtom,
  secondAreaAtom,
  transitioningAtom,
  viewStateAtom,
} from "@/api/atoms";
import { hasSearchCardsQuery } from "@/api/queries";
import { useDetectOutsideClick } from "@/hooks/useDetectOutsideClick";
import { getZoom } from "@/utils";
import { UilAngleDown } from "@iconscout/react-unicons";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useRef } from "react";

const layers = ["country", "county", "city", "area"];

export function LayerDropdown() {
  const dropdownRef = useRef(null);
  const [areaLevel, setAreaLevel] = useAtom(areaLevelAtom);
  const [firstArea, setFirstArea] = useAtom(firstAreaAtom);
  const [secondArea, setSecondArea] = useAtom(secondAreaAtom);
  const viewState = useAtomValue(viewStateAtom);
  const [isActive, setIsActive] = useDetectOutsideClick(dropdownRef, false);
  const [transitioning, setTransitioning] = useAtom(transitioningAtom);
  const mapRef = useAtomValue(mapRefAtom);
  const [_markers, _setMarkers] = useAtom(markersAtom);
  const [hasSearchCards] = useAtom(hasSearchCardsQuery);

  const onClick = () => setIsActive(!isActive);
  const onSelected = ({
    currentTarget,
  }: React.MouseEvent<{
    dataset: DOMStringMap;
  }>) => {
    setTransitioning(true);
    const center = {
      lat: 51.509865,
      lng: -0.118092,
    };
    let zoom = 16;

    switch (currentTarget.dataset.layer) {
      case "country":
        zoom = getZoom("country", true) - 0.5;
        break;
      case "county":
        zoom = getZoom("district", false);
        break;
      case "city":
        zoom = getZoom("city", false);
        break;
      case "area":
        zoom = getZoom("borough", false);
        break;
      default:
        break;
    }

    const duration = Math.abs(viewState.zoom - zoom) * 0.3 * 1000;

    mapRef?.flyTo({
      center,
      zoom,
      duration,
    });

    setFirstArea(null);
    setSecondArea(null);
    setAreaLevel(currentTarget.dataset.layer as AreaLevelType);

    setTimeout(() => {
      setTransitioning(false);
    }, duration);
  };

  const { zoom } = viewState;

  useEffect(() => {
    transitioning && setIsActive(false);
    if (!transitioning) {
      let newAreaLevel = areaLevel;

      switch (true) {
        case zoom <= getZoom("country", true):
          newAreaLevel = "country";
          break;
        case zoom > getZoom("country", true) && zoom < getZoom("district", true):
          newAreaLevel = "county";
          break;
        case zoom >= getZoom("district", true) && zoom < getZoom("city", true):
          if (areaLevel !== "city") {
            setFirstArea(null);
            setSecondArea(null);
          }
          newAreaLevel = "city";
          break;
        case zoom >= getZoom("city", true):
          newAreaLevel = "borough";
          break;
        default:
          break;
      }

      if (newAreaLevel !== areaLevel) {
        const source =
          areaLevel === "country"
            ? "world-countries"
            : areaLevel === "county"
              ? "eng-districts"
              : areaLevel === "city"
                ? "eng-cities"
                : "eng-boroughs";

        if (!hasSearchCards) {
          setFirstArea(null);
          setSecondArea(null);
        }

        if (firstArea?.id)
          mapRef?.setFeatureState({ source, id: firstArea?.id }, { "selected-1": false });

        if (secondArea?.id)
          mapRef?.setFeatureState({ source, id: secondArea?.id }, { "selected-2": false });
        setAreaLevel(newAreaLevel);
      }
    }
  }, [transitioning, zoom]);

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={!transitioning ? onClick : undefined}
        className="flex h-8 w-[132px] cursor-pointer items-center justify-between rounded bg-gray-100 px-4 py-0.5 text-xs font-semibold capitalize"
      >
        <span>{areaLevel === "borough" ? "area" : areaLevel} level</span>
        <UilAngleDown
          className={`transform transition-all duration-200 ${isActive ? "rotate-180" : ""}`}
        />
      </button>
      <aside
        className={`absolute left-0 w-full rounded bg-white transition-all duration-200 ${
          isActive ? "top-11 opacity-100" : "pointer-events-none top-8 select-none opacity-0"
        }`}
      >
        <ul>
          {layers.map((layer) => (
            <li key={layer}>
              <a
                href="#"
                className="block px-4 py-2 text-sm capitalize transition-all duration-200 hover:bg-gray-200"
                onClick={!transitioning ? onSelected : undefined}
                data-layer={layer}
              >
                {layer} level
              </a>
            </li>
          ))}
        </ul>
      </aside>
    </div>
  );
}
